import React, { useEffect } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import MuiSelect from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import CheckIcon from "@material-ui/icons/Check";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Chip from "./Chip";

const useStyles = makeStyles((theme) => ({
  select: {
    overflow: "auto",
    minHeight: 18,
    "&:focus": {
      background: "transparent",
    },
  },
  outlinedInput: {
    background: "#fff",
    height: theme.spacing(6),
  },
  chips: (props) => ({
    display: "flex",
    flexWrap: props.disableWrap ? "" : "wrap",
  }),
  title: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  listText: {
    display: "inline-block",
    width: "75%",
    overflow: "hidden",
  },
  placeholder: {
    color: "#999",
    fontSize: 15,
    marginTop: 1,
  },
  checkIcon: {
    minWidth: "auto",
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 9.5 + ITEM_PADDING_TOP,
      width: 350,
    },
  },
  getContentAnchorEl: null,
  anchorOrigin: {
    vertical: "top",
    horizontal: "left",
  },
  disableAutoFocusItem: true,
};

function Select(props) {
  const {
    placeholder,
    selectedIds = [],
    selectableOptions = [],
    onClose,
    addOption,
    removeOption,
    chipLimit,
    chipCharacterLimit,
    IconComponent,
    label,
    MenuProps: MenuPropsFromProps,
    disableWrap,
    ...other
  } = props;
  const classes = useStyles(props);
  const inputLabel = React.useRef();
  const [labelWidth, setLabelWidth] = React.useState(0);
  const indexedDocs = _.keyBy(selectableOptions, "_id");

  useEffect(() => {
    setLabelWidth(inputLabel.current && inputLabel.current.offsetWidth);
  }, []);

  function handleChange(event, element) {
    const { value: optionId } = element.props;
    const index = selectedIds.indexOf(optionId);
    if (index === -1) {
      addOption(optionId);
    } else {
      removeOption(optionId);
    }
  }

  function handleDelete(event, optionId) {
    removeOption(optionId);
    if (onClose) {
      onClose(event, optionId);
    }
  }

  function renderChips(selected) {
    if (selected.length && !chipLimit) {
      return (
        <div className={classes.chips}>
          {selected.map((selectedId) => {
            const option = indexedDocs[selectedId];
            return option && <Chip key={selectedId} text={option.value} onDelete={handleDelete} characterLimit={chipCharacterLimit} />;
          })}
        </div>
      );
    } else if (selected.length && chipLimit) {
      const underLimit = selected.slice(0, chipLimit);
      const overLimit = selected.slice(chipLimit);
      return (
        <div className={classes.chips}>
          {!!underLimit.length &&
            underLimit.map((selectedId) => {
              const option = indexedDocs[selectedId];
              return option && <Chip key={selectedId} text={option.value} onDelete={handleDelete} characterLimit={chipCharacterLimit} />;
            })}
          {!!overLimit.length && <Chip text={`+${overLimit.length}`} cannotDelete />}
        </div>
      );
    } else {
      return <div className={classes.placeholder}>{placeholder}</div>;
    }
  }

  return (
    <FormControl variant="outlined" fullWidth>
      {label && <InputLabel ref={inputLabel}>{label}</InputLabel>}
      <MuiSelect
        displayEmpty
        classes={{ root: classes.inputRoot, select: classes.select, ...other.classes }}
        value={selectedIds}
        onChange={handleChange}
        onClose={onClose}
        input={<OutlinedInput className={classes.outlinedInput} labelWidth={labelWidth} autoFocus={false} />}
        renderValue={(selected) => renderChips(selected)}
        MenuProps={{ ...MenuProps, ...MenuPropsFromProps }}
        IconComponent={IconComponent}
        {...other}
      >
        {selectableOptions.map((option) => (
          <MenuItem key={option._id} value={option._id} disableRipple>
            <div className={classes.listText}>
              <ListItemText className={classes.title}>{option.value}</ListItemText>
            </div>
            {selectedIds.indexOf(option._id) !== -1 && (
              <ListItemIcon className={classes.checkIcon}>
                <CheckIcon />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
}

Select.propTypes = {
  /** All option ids that are selected */
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** All selectable option objects */
  selectableOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Callback to add a option to array of selectedIds */
  addOption: PropTypes.func.isRequired,
  /** Callback to remove a option from array of selectedIds */
  removeOption: PropTypes.func.isRequired,
  /** Callback fired when popover closes */
  onClose: PropTypes.func,
  /** Text that renders when selectedIds array is empty */
  placeholder: PropTypes.string,
  /** If provided, this is the max number of chips that will display followed by a generic chip showing remainder (e.g. "+2") */
  chipLimit: PropTypes.number,
  /** If provided, this is the max number of characters that will display in a chip */
  chipCharacterLimit: PropTypes.number,
  /** Function that returns an icon component */
  IconComponent: PropTypes.func,
  /** Label to display on input */
  label: PropTypes.string,
  /** Props to pass to Menu component */
  MenuProps: PropTypes.object,
  /** If true, chips will not wrap */
  disableWrap: PropTypes.bool,
};

export default Select;



// WEBPACK FOOTER //
// ./src/components/Select.jsx